import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { TrendingUp, Flag, Users, Calendar, ArrowLeft } from 'lucide-react'

export default function ProgressTrackingPage() {
  const navigate = useNavigate()
  const [milestones, setMilestones] = useState([
    { id: 1, name: 'Design Complete', dueDate: '2026-05-15', status: 'Completed', progress: 100, dependencies: 0 },
    { id: 2, name: 'MVP Development', dueDate: '2026-06-15', status: 'In Progress', progress: 65, dependencies: 1 },
    { id: 3, name: 'Testing Phase', dueDate: '2026-06-30', status: 'Pending', progress: 0, dependencies: 2 },
    { id: 4, name: 'Launch', dueDate: '2026-07-15', status: 'Pending', progress: 0, dependencies: 3 },
  ])

  const stats = useMemo(() => ({
    total: milestones.length,
    completed: milestones.filter(m => m.status === 'Completed').length,
    inProgress: milestones.filter(m => m.status === 'In Progress').length,
    avgProgress: Math.round(milestones.reduce((sum, m) => sum + m.progress, 0) / milestones.length)
  }), [milestones])

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-600/20 text-green-400'
      case 'In Progress': return 'bg-blue-600/20 text-blue-400'
      case 'Pending': return 'bg-slate-600/20 text-slate-400'
      case 'At Risk': return 'bg-red-600/20 text-red-400'
      default: return 'bg-slate-600/20 text-slate-400'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/projects')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Project Management
        </button>
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Progress Tracking</h1>
          <p className="text-slate-400">Monitor project milestones and deliverables</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Milestones</div>
            <div className="text-3xl font-bold text-blue-400">{stats.total}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Completed</div>
            <div className="text-3xl font-bold text-green-400">{stats.completed}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">In Progress</div>
            <div className="text-3xl font-bold text-yellow-400">{stats.inProgress}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Avg Progress</div>
            <div className="text-3xl font-bold text-purple-400">{stats.avgProgress}%</div>
          </div>
        </div>

        <div className="space-y-4">
          {milestones.map((milestone, index) => (
            <div key={milestone.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Flag size={20} className="text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">{milestone.name}</h3>
                    <span className={`px-3 py-1 rounded text-xs font-semibold ${getStatusColor(milestone.status)}`}>{milestone.status}</span>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">
                    Due: {new Date(milestone.dueDate).toLocaleDateString()} • Depends on {milestone.dependencies} task(s)
                  </p>

                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-400">Overall Progress</span>
                      <span className="text-sm font-bold text-white">{milestone.progress}%</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-3">
                      <div className={`h-full rounded-full bg-gradient-to-r ${milestone.progress >= 80 ? 'from-green-500 to-green-600' : milestone.progress >= 50 ? 'from-yellow-500 to-yellow-600' : 'from-orange-500 to-orange-600'}`} style={{ width: `${milestone.progress}%` }}></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Deliverables</p>
                      <p className="text-white font-semibold mt-1">5/7</p>
                    </div>
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Team Assigned</p>
                      <p className="text-white font-semibold mt-1">8</p>
                    </div>
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Budget Used</p>
                      <p className="text-white font-semibold mt-1">72%</p>
                    </div>
                  </div>
                </div>
              </div>

              {index < milestones.length - 1 && (
                <div className="border-t border-blue-500/20 pt-4 mt-4">
                  <p className="text-xs text-slate-400">Next: {milestones[index + 1].name}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
